/**
 * HOOK: usePaymentActions
 * Actions mutation paiement : confirmation manuelle virement/chèque
 *
 * Depuis review finding C (2026-04-19) : confirmPayment délègue à la RPC
 * `confirm_payment_with_timeline` (migration 00068) pour garantir l'atomicité
 * UPDATE payment_cases + INSERT timeline_events. Si la timeline échoue, le
 * UPDATE est rollbacké — plus de paiement confirmé sans trace.
 */

import { useCallback } from 'react';
import { useSupabase } from './useSupabase';

interface ConfirmPaymentParams {
  paymentCaseId: string;
  comment: string;
  contactId: string;
  // Contact identifié comme payeur (TICKET-PAY-01 — review-blocker-01).
  // Obligatoire : persisté dans payment_cases.payer_contact_id et dans le
  // metadata de l'event timeline PAYMENT_PAID pour cohérence audit.
  payerContactId: string;
  // BED uniquement (TICKET-BED-25, migration 00294) : montant réellement reçu,
  // en centimes. Absent = le restant dû entier. En dessous du restant dû, le
  // dossier passe en `partial` et le lien de paiement reste actif.
  amountCents?: number;
  // BED uniquement : moyen du règlement reçu. Obligatoire sur un dossier
  // `partial` (le statut ne dit pas virement ou chèque).
  method?: 'transfer' | 'cheque';
  // BED uniquement (00295) : restant dû AFFICHÉ au staff. Le serveur refuse
  // (remaining_changed) s'il a changé depuis : écran périmé, double saisie.
  expectedRemainingCents?: number;
}

export interface ConfirmPaymentResult {
  success: boolean;
  // Message d'erreur traductible remonte a l'UI quand success=false.
  // Ex : conflit de payeur detecte par la garde anti-overwrite (review-blocker-A2).
  error?: string;
  // Retour de la RPC (00294) quand success=true : `paid` (soldé, mail de
  // confirmation à envoyer) ou `partial` (versement partiel, reliquat dû).
  status?: string;
  remainingCents?: number | null;
  // 00295 : montant encaissé au-delà du montant du dossier (absent sinon).
  overpaidCents?: number | null;
}

// Remboursement de trop-perçu NOTÉ (00295) : le remboursement est fait à la
// main hors CRM, la RPC n'en garde que la trace.
interface RecordRefundParams {
  paymentCaseId: string;
  amountCents: number;
  // Date du remboursement, format YYYY-MM-DD (jamais dans le futur).
  refundedOn: string;
  comment: string;
}

export interface RecordRefundResult {
  success: boolean;
  error?: string;
  remainingToRefundCents?: number;
}

interface RestorePaymentParams {
  paymentCaseId: string;
  // Motif obligatoire trace en timeline (event payment_restored, migration 00127).
  reason: string;
}

// Mapping code erreur PG (RAISE EXCEPTION dans la RPC) → message UI.
function mapRpcError(message: string): ConfirmPaymentResult {
  // Migration 00106 : nouvelles gardes statut deja terminal.
  if (message.includes('payment_already_paid')) {
    return {
      success: false,
      error: 'Ce paiement a déjà été confirmé. Veuillez rafraîchir la page.',
    };
  }
  if (message.includes('payment_status_terminal')) {
    return {
      success: false,
      error:
        'Ce dossier est dans un statut terminal (expiré ou échoué) et ne peut plus être confirmé manuellement.',
    };
  }
  // Migration 00123 : allowlist statut. Seuls virement/chèque/CB en attente sont
  // confirmables manuellement ; un partial (BED) ou draft est rejeté côté serveur.
  if (message.includes('payment_not_confirmable')) {
    return {
      success: false,
      error:
        "Ce dossier n'est pas en attente d'une confirmation manuelle (seuls un virement, un chèque ou une CB en attente peuvent être confirmés).",
    };
  }
  // Migration 00294 (BED-25) : confirmation à montant saisi. Un montant
  // supérieur au restant dû est accepté depuis 00295 (trop-perçu).
  if (message.includes('remaining_changed')) {
    return {
      success: false,
      error:
        "Le restant dû a changé depuis l'ouverture de la fiche (versement enregistré ou montant du dossier modifié entre-temps) : rafraîchissez la page avant de saisir.",
    };
  }
  if (message.includes('card_payment_in_progress')) {
    return {
      success: false,
      error:
        'Un paiement par carte est en cours sur ce dossier : réessayez dans 30 minutes au plus, une fois le restant dû à jour.',
    };
  }
  if (message.includes('nothing_due')) {
    return {
      success: false,
      error:
        "Plus rien n'est dû sur ce dossier : il sera soldé automatiquement. Veuillez rafraîchir la page.",
    };
  }
  if (message.includes('amount_required')) {
    return {
      success: false,
      error: 'Le restant dû a changé : veuillez rafraîchir la page puis saisir le montant reçu.',
    };
  }
  if (message.includes('amount_invalid')) {
    return { success: false, error: 'Le montant reçu doit être supérieur à 0.' };
  }
  if (message.includes('offline_method_mismatch') || message.includes('invalid_offline_method')) {
    return {
      success: false,
      error: 'Le moyen de paiement ne correspond pas au dossier. Veuillez rafraîchir la page.',
    };
  }
  if (message.includes('partial_confirmation_bed_only')) {
    return {
      success: false,
      error: 'Un montant partiel ne peut être saisi que sur un solde BED.',
    };
  }
  if (message.includes('manual_card_confirmation_bpi_only')) {
    return {
      success: false,
      error:
        "La régularisation CB ancienne plateforme ne s'applique qu'aux BPI, pas aux dossiers BED.",
    };
  }
  if (message.includes('manual_card_confirmation_forbidden')) {
    return {
      success: false,
      error: 'Seule la compta peut confirmer manuellement un paiement CB.',
    };
  }
  if (message.includes('payment_card_method_required')) {
    return {
      success: false,
      error: "Ce dossier n'est pas un paiement CB en attente.",
    };
  }
  // Migrations 00074/00075 : validations contact/payeur membre du dossier.
  if (message.includes('contact_not_in_case')) {
    return {
      success: false,
      error: "Le contact sélectionné n'appartient pas à ce dossier.",
    };
  }
  if (message.includes('payer_contact_id_not_in_case')) {
    return {
      success: false,
      error: "Le payeur sélectionné n'appartient pas à ce dossier.",
    };
  }
  // Defense-in-depth (devient inatteignable avec 00106 mais conserve pour filet).
  if (message.includes('payer_conflict')) {
    return {
      success: false,
      error: 'Le dossier a changé de statut (payé, expiré ou échoué). Veuillez rafraîchir la page.',
    };
  }
  if (message.includes('authentication_required')) {
    return { success: false, error: 'Session expirée. Veuillez vous reconnecter.' };
  }
  if (message.includes('payment_case_not_found')) {
    return { success: false, error: 'Dossier de paiement introuvable.' };
  }
  if (message.includes('comment_required')) {
    return { success: false, error: 'Commentaire obligatoire.' };
  }
  if (message.includes('payer_required')) {
    return { success: false, error: 'Payeur obligatoire.' };
  }
  return { success: false };
}

// Mapping des codes d'erreur de restore_expired_payment_case (migration 00127)
// vers messages UI. Exporte pour test unitaire (pas de dependance React).
export function mapRestoreError(message: string): ConfirmPaymentResult {
  // current_user_role() hors admin/superadmin -> 42501. Couvre aussi le cas non
  // authentifie (role null -> NOT IN (...) -> access_denied).
  if (message.includes('access_denied')) {
    return {
      success: false,
      error: "Vous n'avez pas les droits pour ré-ouvrir un dossier (réservé aux administrateurs).",
    };
  }
  if (message.includes('reason_required')) {
    return { success: false, error: 'Motif obligatoire.' };
  }
  // Course : un autre admin a déjà restauré, ou le dossier n'a jamais été expiré.
  if (message.includes('payment_not_expired')) {
    return {
      success: false,
      error: "Ce dossier n'est pas (ou plus) expiré. Veuillez rafraîchir la page.",
    };
  }
  if (message.includes('payment_case_not_found')) {
    return { success: false, error: 'Dossier de paiement introuvable.' };
  }
  return { success: false };
}

// Mapping des codes d'erreur de record_payment_refund (migration 00295).
// Exporté pour test unitaire.
export function mapRefundError(message: string): RecordRefundResult {
  if (message.includes('refund_exceeds_overpayment')) {
    return {
      success: false,
      error: 'Le montant dépasse le trop-perçu restant à rembourser. Veuillez rafraîchir la page.',
    };
  }
  if (message.includes('refund_date_invalid')) {
    return {
      success: false,
      error:
        "La date du remboursement doit être comprise entre la création du dossier et aujourd'hui.",
    };
  }
  if (message.includes('refund_case_not_paid')) {
    return {
      success: false,
      error:
        "Le dossier n'est pas encore soldé : confirmez d'abord le paiement (ou attendez le rapprochement automatique), puis notez le remboursement.",
    };
  }
  if (message.includes('refund_bed_only')) {
    return { success: false, error: 'Un remboursement ne se note que sur un solde BED.' };
  }
  if (message.includes('refund_forbidden')) {
    return { success: false, error: "Vous n'avez pas les droits pour noter un remboursement." };
  }
  if (message.includes('amount_invalid')) {
    return { success: false, error: 'Le montant remboursé doit être supérieur à 0.' };
  }
  if (message.includes('comment_required')) {
    return { success: false, error: 'Commentaire obligatoire.' };
  }
  if (message.includes('authentication_required')) {
    return { success: false, error: 'Session expirée. Veuillez vous reconnecter.' };
  }
  if (message.includes('payment_case_not_found')) {
    return { success: false, error: 'Dossier de paiement introuvable.' };
  }
  return { success: false };
}

export function usePaymentActions() {
  const supabase = useSupabase();

  const confirmPayment = useCallback(
    async ({
      paymentCaseId,
      comment,
      contactId,
      payerContactId,
      amountCents,
      method,
      expectedRemainingCents,
    }: ConfirmPaymentParams): Promise<ConfirmPaymentResult> => {
      try {
        // RPC atomique : UPDATE payment_cases + INSERT timeline_events
        // dans une seule transaction (migration 00068, review finding C).
        // Montant et moyen ne sont transmis que s'ils sont fournis (BED) : un
        // BPI garde l'appel à 4 arguments.
        const { data, error } = await supabase.rpc('confirm_payment_with_timeline', {
          p_payment_case_id: paymentCaseId,
          p_payer_contact_id: payerContactId,
          p_comment: comment,
          p_contact_id: contactId,
          ...(amountCents !== undefined ? { p_amount_cents: amountCents } : {}),
          ...(method !== undefined ? { p_method: method } : {}),
          ...(expectedRemainingCents !== undefined
            ? { p_expected_remaining_cents: expectedRemainingCents }
            : {}),
        });

        if (error) {
          console.error('[usePaymentActions] confirmPayment RPC error:', error);
          return mapRpcError(error.message ?? '');
        }

        const result = (data ?? {}) as {
          status?: string;
          remaining_cents?: number | null;
          overpaid_cents?: number | null;
        };
        return {
          success: true,
          status: result.status,
          remainingCents: result.remaining_cents,
          overpaidCents: result.overpaid_cents,
        };
      } catch (err) {
        console.error('[usePaymentActions] confirmPayment error:', err);
        return { success: false };
      }
    },
    [supabase]
  );

  // Restauration manuelle d'un dossier expire (admin/superadmin). Delegue a la
  // RPC restore_expired_payment_case (00127, 00321) : expired -> draft, ou
  // pending_transfer/pending_cheque si virement/chèque déjà choisi, reset compteurs
  // de relance, re-active le crew archive, trace timeline_event payment_restored.
  // La garde de role est ENFORCEE cote serveur (current_user_role) ; le gating UI
  // n'est qu'un confort.
  const restoreExpiredPaymentCase = useCallback(
    async ({ paymentCaseId, reason }: RestorePaymentParams): Promise<ConfirmPaymentResult> => {
      try {
        const { error } = await supabase.rpc('restore_expired_payment_case', {
          p_payment_case_id: paymentCaseId,
          p_reason: reason,
        });

        if (error) {
          console.error('[usePaymentActions] restoreExpiredPaymentCase RPC error:', error);
          return mapRestoreError(error.message ?? '');
        }

        return { success: true };
      } catch (err) {
        console.error('[usePaymentActions] restoreExpiredPaymentCase error:', err);
        return { success: false };
      }
    },
    [supabase]
  );

  // Note un remboursement de trop-perçu (00295). Plafond, rôle et date
  // contrôlés côté serveur.
  const recordRefund = useCallback(
    async ({
      paymentCaseId,
      amountCents,
      refundedOn,
      comment,
    }: RecordRefundParams): Promise<RecordRefundResult> => {
      try {
        const { data, error } = await supabase.rpc('record_payment_refund', {
          p_payment_case_id: paymentCaseId,
          p_amount_cents: amountCents,
          p_refunded_on: refundedOn,
          p_comment: comment,
        });

        if (error) {
          console.error('[usePaymentActions] recordRefund RPC error:', error);
          return mapRefundError(error.message ?? '');
        }

        const result = (data ?? {}) as { remaining_to_refund_cents?: number };
        return { success: true, remainingToRefundCents: result.remaining_to_refund_cents };
      } catch (err) {
        console.error('[usePaymentActions] recordRefund error:', err);
        return { success: false };
      }
    },
    [supabase]
  );

  return { confirmPayment, restoreExpiredPaymentCase, recordRefund };
}
